import {
  PlaygroundLocalMsg,
  usePlaygroundLocalMsg,
} from "@faire/prototype-playground/platform/localization";
import { Add } from "@faire/slate-icons/Add";
import { ChevronLeft } from "@faire/slate-icons/ChevronLeft";
import { Button } from "@faire/slate/components/button";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";

import RetailerLayout from "../../../components/RetailerLayout";
import { CompassProvider } from "../../../contexts/CompassContext";

import { CollectionCard } from "./CollectionCard";
import {
  CART_ROUTE,
  HOME_ROUTE,
  SAVED_PRODUCTS_ROUTE,
  collectionDetailRoute,
} from "./routes";
import {
  ALL_FAVORITES_COLLECTION_ID,
  setCollections,
  useCollections,
  useProductTokensByCollection,
} from "./savedStore";
import { useAccountDropdown } from "./useAccountDropdown";
import {
  SAVE_FLOW_POPOVER,
  useFavoritesController,
} from "./useFavoritesController";

/** Every collection in a wrapping grid, reached from the "All collections" tile. */
export default function SavedProductsAllCollectionsPage() {
  const history = useHistory();
  const favorites = useFavoritesController({
    saveFlowVariant: SAVE_FLOW_POPOVER,
  });
  const accountDropdown = useAccountDropdown();
  const collections = useCollections();
  const productTokensByCollection = useProductTokensByCollection();

  const pageTitle = usePlaygroundLocalMsg("All collections");
  const savedProductsLabel = usePlaygroundLocalMsg("Saved products");
  const newCollectionName = usePlaygroundLocalMsg("New collection");

  useEffect(() => {
    document.title = `Saved Products & Cart — ${pageTitle}`;
  }, [pageTitle]);

  const handleNewCollection = () => {
    const customCollectionCount = collections.length;
    setCollections((current) => [
      ...current,
      {
        id: `collection-${Date.now()}`,
        name: `${newCollectionName} ${customCollectionCount}`,
      },
    ]);
  };

  return (
    <CompassProvider>
      <RetailerLayout
        logoLinkUrl={HOME_ROUTE}
        cartHref={CART_ROUTE}
        onCartClick={() => history.push(CART_ROUTE)}
        cartCount={favorites.savedCount}
        accountDropdown={accountDropdown}
      >
        <div className="mx-auto flex w-full max-w-[1920px] flex-col gap-6 px-6 pt-8 pb-16 lg:px-12">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="flex flex-col gap-1">
              <button
                type="button"
                onClick={() => history.push(SAVED_PRODUCTS_ROUTE)}
                className="text-fs-text-primary hover:text-fs-text-subdued -ml-1 flex items-center gap-0.5 self-start transition-colors"
              >
                <ChevronLeft width="16" height="16" />
                <span className="type-fs-label-medium">{savedProductsLabel}</span>
              </button>
              <h1 className="type-fs-page-heading text-fs-text-primary">
                <PlaygroundLocalMsg defaultMessage="All collections" />
              </h1>
            </div>
            <Button
              variant="tertiary"
              size="small"
              iconProps={{ Component: Add, position: "start" }}
              onClick={handleNewCollection}
            >
              <PlaygroundLocalMsg defaultMessage="New collection" />
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            {collections.map((collection) => (
              <CollectionCard
                key={collection.id}
                collection={collection}
                productTokensByCollection={productTokensByCollection}
                fluid
                label={
                  collection.id === ALL_FAVORITES_COLLECTION_ID
                    ? savedProductsLabel
                    : undefined
                }
                onClick={() =>
                  history.push(collectionDetailRoute(collection.id))
                }
              />
            ))}
          </div>
        </div>
      </RetailerLayout>
      {favorites.popoverElement}
      {favorites.toastElement}
      {favorites.collectionToastElement}
    </CompassProvider>
  );
}
